import { FeedItemType } from '../enums';

/**
 * Feed is a discovery system, not merely a post list.
 *
 * Layers:
 * - creator discovery surface
 * - post visibility layer
 * - ranking input-ready system (signals attached, ranking unchanged for now)
 */
export interface FeedDiscoverySystem {
  readonly creatorDiscovery: true;
  readonly postVisibility: true;
  readonly rankingInputReady: true;
}

export const FEED_DISCOVERY_SYSTEM: FeedDiscoverySystem = {
  creatorDiscovery: true,
  postVisibility: true,
  rankingInputReady: true,
};

/** Base shape shared by all feed item types for future unified ranking. */
export interface FeedItemBase {
  id: string;
  type: FeedItemType;
  createdAt: Date;
  creatorId: string;
}

export interface FeedPostItem extends FeedItemBase {
  type: FeedItemType.POST;
  postId: string;
  contentPreview: string;
}

/** Creator profile surfaced in discovery feed (future use). */
export interface FeedCreatorProfileItem extends FeedItemBase {
  type: FeedItemType.CREATOR_PROFILE;
  displayName: string | null;
  topics: string[];
}

/** Product placeholder for future commerce discovery in feed. */
export interface FeedProductItem extends FeedItemBase {
  type: FeedItemType.PRODUCT;
  productId: string;
  title: string;
  price: number;
}

export type FeedDiscoveryItem = FeedPostItem | FeedCreatorProfileItem | FeedProductItem;

/** Tags which discovery layer an item primarily serves. */
export type FeedDiscoveryLayer = 'creator_discovery' | 'post_visibility' | 'ranking_input';

export function getFeedItemDiscoveryLayer(type: FeedItemType): FeedDiscoveryLayer {
  switch (type) {
    case FeedItemType.CREATOR_PROFILE:
      return 'creator_discovery';
    case FeedItemType.PRODUCT:
      return 'ranking_input';
    case FeedItemType.POST:
    default:
      return 'post_visibility';
  }
}
